// GtHome — the operator command centre. Same layout family as the records
// (gtr2, 12px): a greeting banner with the four numbers that matter, then the
// work queue and the activity feed on the left, pipeline and attention on the right.
//
// Reads the event spine (audit_log via /api/home/activity), not a UNION across
// source tables, so anything that calls recordEvent appears here with no change
// to this screen. Events arrive already decorated by the server (title, group,
// subject, detail_line, link), because every consumer needs the same sentence
// and each screen inventing its own wording is how the old feed ended up showing
// raw Clerk ids next to machine action names.
//
// The work queue leads, above the feed: opening the app should answer "what
// needs me", not "what happened". Each queue group is a doorway into the real
// filtered table, because "and 3 more" is useless when it is 500.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import API_BASE from '../../config';
import GtLoader from '../../components/GtLoader';
import { paths } from '../../components/gtRoutes';
import GtHomeWatch from './GtHomeWatch';
import GtNoticeModal from '../../components/GtNoticeModal';
import GtBulkNoticeModal from '../../components/GtBulkNoticeModal';
import { Ico, ICONS, Stat } from '../../components/GtRecordKit';

// Colour by semantic group, not by entity, so a win reads as a win wherever it
// came from. Unknown groups fall back to slate rather than breaking.
const GROUP = {
  candidate:  { c: '#2563EB', bg: 'rgba(59,130,246,.11)', l: 'Candidate' },
  role:       { c: '#6D28D9', bg: 'rgba(139,92,246,.11)', l: 'Role' },
  client:     { c: '#B4740B', bg: 'rgba(245,166,35,.15)', l: 'Client' },
  pipeline:   { c: '#4F46E5', bg: 'rgba(79,70,229,.11)',  l: 'Pipeline' },
  delivery:   { c: '#0E7490', bg: 'rgba(14,116,144,.11)', l: 'Delivery' },
  clientact:  { c: '#17838A', bg: 'rgba(31,169,178,.12)', l: 'Client activity' },
  win:        { c: '#00A878', bg: 'rgba(0,168,120,.13)',  l: 'Win' },
  loss:       { c: '#C81E3E', bg: 'rgba(244,63,94,.10)',  l: 'Loss' },
  ai:         { c: '#17838A', bg: 'rgba(31,169,178,.12)', l: 'AI' },
  intel:      { c: '#6D28D9', bg: 'rgba(139,92,246,.11)', l: 'Intelligence' },
  note:       { c: '#64748B', bg: 'rgba(100,116,139,.11)', l: 'Note' },
  compliance: { c: '#C81E3E', bg: 'rgba(244,63,94,.10)',  l: 'Compliance' },
  other:      { c: '#64748B', bg: 'rgba(100,116,139,.11)', l: 'Event' },
};
function groupOf(g) { return GROUP[g] || GROUP.other; }

// Filter chips map to actor_kind: who was acting, which is the question a
// god's-eye view is really answering.
const KIND_CHIPS = [
  { key: null,        label: 'Everything' },
  { key: 'internal',  label: 'Team' },
  { key: 'candidate', label: 'Candidates' },
  { key: 'client',    label: 'Clients' },
  { key: 'ai',        label: 'AI' },
  { key: 'system',    label: 'System' },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}
function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24) return h + 'h ago';
  const days = Math.floor(h / 24);
  if (days < 30) return days + 'd ago';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function money(n) {
  return '\u00A3' + Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

export default function GtHome({ getToken }) {
  const navigate = useNavigate();
  const { user } = useUser();

  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState(null);
  const [facets, setFacets] = useState(null);
  const [watch, setWatch] = useState(null);
  // Held in a ref so authFetch does not change identity when the name resolves,
  // which would re-fire every load effect.
  const actorNameRef = useRef(null);
  const [events, setEvents] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [more, setMore] = useState(false);
  const [kind, setKind] = useState(null);
  const [error, setError] = useState(null);

  const actorName = user?.fullName || user?.primaryEmailAddress?.emailAddress || null;
  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const r = await fetch(API_BASE + path, {
      ...opts,
      headers: {
        ...(opts.headers || {}),
        Authorization: 'Bearer ' + token,
        // Clerk's session token carries no name, so the browser supplies it.
        ...(actorNameRef.current ? { 'X-Gt-Actor-Name': actorNameRef.current } : {}),
      },
    });
    if (!r.ok) {
      let body = null;
      try { body = await r.json(); } catch (e) { /* non-JSON error */ }
      const err = new Error((body && body.error) || ('Request failed: ' + r.status));
      err.status = r.status;
      throw err;
    }
    return r.json();
  }, [getToken]);

  // Stats, queue and facets load once. The feed reloads whenever the chip
  // changes, which is why it is a separate effect.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [s, q, f, w] = await Promise.all([
          authFetch('/api/home/stats'),
          authFetch('/api/home/queue'),
          authFetch('/api/home/activity-facets'),
          authFetch('/api/home/watch'),
        ]);
        if (!live) return;
        setStats(s); setQueue(q); setFacets(f); setWatch(w);
      } catch (e) { if (live) setError(e.message); }
    })();
    return () => { live = false; };
  }, [authFetch]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const a = await authFetch('/api/home/activity?limit=25' + (kind ? '&kind=' + kind : ''));
        if (!live) return;
        setEvents(a.events || []);
        setCursor(a.next_before || null);
        setHasMore(!!a.has_more);
      } catch (e) { if (live) setError(e.message); }
    })();
    return () => { live = false; };
  }, [authFetch, kind]);

  // Candidates whose application moved and who have not been told. Nothing is
  // ever sent without a person choosing, so these wait here until someone does.
  const [notices, setNotices] = useState(null);
  const [noticeOpen, setNoticeOpen] = useState(null);
  const [noticeBulk, setNoticeBulk] = useState(null);
  // Queue groups opened past their first few rows.
  const [openGroups, setOpenGroups] = useState({});
  const loadNotices = useCallback(async () => {
    try {
      const r = await authFetch('/api/notices?state=pending');
      setNotices(r.notices || []);
    } catch (e) { setNotices([]); }
  }, [authFetch]);
  useEffect(() => { loadNotices(); }, [loadNotices]);

  async function loadMore() {
    if (!cursor || more) return;
    setMore(true);
    try {
      const a = await authFetch('/api/home/activity?limit=25' + (kind ? '&kind=' + kind : '')
        + '&before=' + encodeURIComponent(cursor));
      setEvents(prev => [...(prev || []), ...(a.events || [])]);
      setCursor(a.next_before || null);
      setHasMore(!!a.has_more);
    } catch (e) { setError(e.message); }
    setMore(false);
  }

  // The server decides where an event points, by ref, never by integer id.
  function openEvent(ev) {
    const l = ev.link;
    if (!l || !l.ref) return;
    if (l.type === 'candidate') navigate(paths.candidate(l.ref));
    else if (l.type === 'role') navigate(paths.role(l.ref));
    else if (l.type === 'client') navigate(paths.client(l.ref));
  }

  actorNameRef.current = actorName;
  const name = user?.firstName || 'there';
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const tiles = stats ? [
    { n: stats.candidates, l: 'In the pool', sub: Number(stats.candidates_7d) > 0 ? '+' + stats.candidates_7d + ' this week' : 'no new this week', to: paths.candidates() },
    { n: stats.open_roles, l: 'Open roles', sub: 'across ' + stats.clients + ' client' + (Number(stats.clients) === 1 ? '' : 's'), to: paths.roles() },
    { n: stats.in_play, l: 'In play', sub: 'submitted, not closed', to: null },
    { n: stats.placements, l: 'Placements', sub: Number(stats.fees_placed) > 0 ? money(stats.fees_placed) + ' in fees' : 'no fees recorded yet', to: null },
  ] : [];

  // Every group states what happened and what it blocks. The earlier version
  // had three groups labelled things like "client said yes", which told you
  // nothing about what to do. Ordered by how much it costs to ignore.
  const queueGroups = queue ? [
    { key: 'applications_new', label: 'New applications, not yet reviewed',
      why: 'Somebody applied through the job board and nobody has looked yet. This is how a good candidate goes cold.',
      rows: queue.applications_new, tone: 'go', all: null,
      line: r => [
        r.days_waiting === 0 ? 'applied today'
          : (r.days_waiting != null ? 'waiting ' + r.days_waiting + 'd' : null),
        r.role, r.client_name
      ].filter(Boolean).join(' \u00B7 ') },

    { key: 'interview_not_progressed', label: 'Interview requested, not progressed',
      why: 'The client asked to interview and nothing has been logged since.',
      rows: queue.interview_not_progressed, tone: 'go', all: null,
      line: r => [r.client_name, r.role].filter(Boolean).join(' \u00B7 ')
        + (r.days_waiting != null ? ' \u00B7 waiting ' + r.days_waiting + 'd' : '') },

    { key: 'right_to_work_unknown', label: 'Right to work unverified',
      why: 'Sent to a client without knowing they can legally take the job. This blocks placement.',
      rows: queue.right_to_work_unknown, tone: 'stop',
      all: '/candidates?preset=rtw_unknown',
      line: r => [r.client_name, r.role].filter(Boolean).join(' \u00B7 ') },

    { key: 'share_expiring', label: 'Link expiring while being read',
      why: 'The client is still opening it and access is about to lapse.',
      rows: queue.share_expiring, tone: 'warn', all: null,
      line: r => 'opened ' + r.view_count + '\u00D7 \u00B7 ' + r.days_left + 'd left' },

    { key: 'parser_unsure', label: 'Parser was unsure',
      why: 'These fields were guessed. Check them against the CV before submitting.',
      rows: queue.parser_unsure, tone: 'warn', all: '/candidates?preset=flagged',
      line: null },

    { key: 'no_recruiter', label: 'No recruiter assigned',
      why: 'Nobody is accountable for filling this role.',
      rows: queue.no_recruiter, tone: 'flat', all: '/roles?preset=unowned',
      line: r => [r.client_name, r.days_open != null ? 'open ' + r.days_open + 'd' : null].filter(Boolean).join(' \u00B7 ') },

    { key: 'human_review_open', label: 'Human review requested',
      why: 'A candidate asked for a person to look again at an AI-assisted decision. Article 22A: this has a clock on it.',
      rows: queue.human_review_open, tone: 'stop', all: null,
      line: r => [r.role, r.days_waiting != null ? 'waiting ' + r.days_waiting + 'd' : null].filter(Boolean).join(' \u00B7 ') },

    { key: 'consent_missing', label: 'No consent recorded',
      why: 'In play with no lawful basis logged. Needed before any outreach.',
      rows: queue.consent_missing, tone: 'stop', all: '/candidates?preset=no_consent',
      line: () => 'consent not captured' },

    { key: 'retention_unset', label: 'No retention expiry',
      why: 'Held indefinitely. The ICO named this specifically as a failure.',
      rows: queue.retention_unset, tone: 'warn', all: null,
      line: r => 'held ' + r.days_held + 'd' },

    { key: 'fee_not_captured', label: 'Placed, fee not recorded',
      why: 'Revenue earned and never entered.',
      rows: queue.fee_not_captured, tone: 'stop', all: null,
      line: r => [r.client_name, r.role].filter(Boolean).join(' \u00B7 ') },
  ].filter(g => Array.isArray(g.rows)) : [];

  const queueTotal = queueGroups.reduce((n, g) => n + g.rows.length, 0) + (notices ? notices.length : 0);
  const kindCount = (k) => {
    if (!facets) return null;
    if (!k) return facets.total;
    const row = (facets.kinds || []).find(x => x.actor_kind === k);
    return row ? row.n : 0;
  };

  if (error) return <div className="gtr2 gth2"><div className="gtc-error">{error}</div></div>;
  if (!stats || !queue) return <GtLoader />;
  // One list of everything that needs a person, so the groups can be laid out
  // and shortened the same way. Notices lead because a candidate is waiting.
  const allGroups = [];
  if (notices && notices.length) {
    allGroups.push({ key: 'notices', label: 'Candidate not yet told', tone: 'warn', notice: true,
      why: 'Their application moved and they have not heard. Nothing is sent until you choose.', rows: notices });
  }
  queueGroups.filter(g => g.rows.length).forEach(g => allGroups.push(g));
  const stopCount = allGroups.filter(g => g.tone === 'stop').reduce((n, g) => n + g.rows.length, 0);
  const SHOW = 4;

  function openQueueRow(g, r) {
    if (g.notice) { if (r.candidate && r.candidate.ref) navigate(paths.candidate(r.candidate.ref)); return; }
    if (r.candidate_ref) { navigate(paths.candidate(r.candidate_ref)); return; }
    if (g.key === 'no_recruiter' && r.ref) { navigate(paths.role(r.ref)); return; }
    if (r.client_ref) { navigate(paths.client(r.client_ref)); return; }
    if (r.ref) navigate(paths.role(r.ref));
  }

  return (
    <>
    <div className="gtr2 gth2">
      <section className="gtr2-hero gth2-hero">
        <div className="gth2-banner">
          <div className="gth2-greet">
            <span className="gth2-date">{today}</span>
            <h1 className="gtr2-name">{greeting()}, {name}</h1>
            <p className="gth2-summary">
              {queueTotal === 0
                ? 'Nothing is waiting on you. Everything submitted is with a client.'
                : queueTotal + (queueTotal === 1 ? ' thing needs' : ' things need') + ' you' + (stopCount ? ', ' + stopCount + ' of them blocking' : '') + '.'}
            </p>
          </div>
          <div className="gth2-pulse">
            <span className={'gth2-pulse-dot' + (queueTotal ? (stopCount ? ' gth2-pulse-dot--stop' : ' gth2-pulse-dot--warn') : '')} />
            <span>{queueTotal ? 'Needs you' : 'All clear'}</span>
            <b>{queueTotal}</b>
          </div>
        </div>
        <div className="gtr2-stats gth2-stats">
          {tiles.map((t, i) => (
            <Stat key={i} label={t.l} value={t.n} sub={t.sub} onClick={t.to ? () => navigate(t.to) : undefined} />
          ))}
        </div>
      </section>

      <div className="gtr2-body gth2-body">
        <div className="gtr2-main">
          <section className="gtr-card gth2-card">
            <div className="gtr-cardh">
              <span className="gth2-h">Needs you <span className="gtr2-count">{queueTotal}</span></span>
              <span className="gth2-legend">
                <span><i className="gth2-dot gth2-dot--stop" />Blocking</span>
                <span><i className="gth2-dot gth2-dot--warn" />At risk</span>
                <span><i className="gth2-dot gth2-dot--go" />Waiting</span>
              </span>
            </div>
            {allGroups.length === 0 ? (
              <div className="gth2-clear">
                <span className="gth2-clear-ico"><Ico d={ICONS.check} size={18} /></span>
                <b>All clear</b>
                <span>Everything submitted is with a client, and no CV needs review.</span>
              </div>
            ) : (
              <div className="gth2-queue">
                {allGroups.map(g => {
                  const expanded = !!openGroups[g.key];
                  const rows = expanded ? g.rows : g.rows.slice(0, SHOW);
                  return (
                    <div key={g.key} className={'gth2-q gth2-q--' + g.tone}>
                      <div className="gth2-qhead">
                        <i className={'gth2-dot gth2-dot--' + g.tone} />
                        <b>{g.label}</b>
                        <span className="gth2-qn">{g.rows.length}</span>
                      </div>
                      <p className="gth2-qwhy">{g.why}</p>
                      <ul className="gth2-qlist">
                        {rows.map((r, i) => (
                          <li key={g.notice ? r.id : i} className="gth2-qrow">
                            <button type="button" className="gth2-qname" onClick={() => openQueueRow(g, r)}>
                              {g.notice ? r.candidate.name : (r.full_name || r.title || r.ref)}
                            </button>
                            <span className="gth2-qmeta">
                              {g.notice
                                ? [r.role.title, r.status_label, r.via === 'client_verdict' ? 'from the client' : null].filter(Boolean).join(' · ')
                                : (g.line ? g.line(r) : null)}
                            </span>
                            {g.key === 'parser_unsure' && Array.isArray(r.flags) ? (
                              <span className="gth2-qfields">
                                {r.flags.map((f, j) => <span key={j} className="gth2-qfield">{f.field}</span>)}
                                {r.flags[0] && r.flags[0].issue ? <span className="gth2-qwhyrow">{r.flags[0].issue}</span> : null}
                              </span>
                            ) : null}
                            {g.notice && <button type="button" className="gtr2-btn gtr2-btn--xs" onClick={() => setNoticeOpen([r.id])}>Decide</button>}
                          </li>
                        ))}
                      </ul>
                      {(g.rows.length > SHOW || g.all || (g.notice && g.rows.length > 1)) && (
                        <div className="gth2-qfoot">
                          {g.rows.length > SHOW && (
                            <button type="button" className="gth2-link" onClick={() => setOpenGroups(o => ({ ...o, [g.key]: !expanded }))}>
                              {expanded ? 'Show fewer' : 'Show all ' + g.rows.length}
                            </button>
                          )}
                          {g.notice && g.rows.length > 1 && (
                            <button type="button" className="gth2-link" onClick={() => setNoticeBulk(g.rows.map(n => n.id))}>Decide all {g.rows.length}</button>
                          )}
                          {g.all && (
                            <button type="button" className="gth2-link" onClick={() => navigate(g.all)}>Open in the table<Ico d={ICONS.arrow} size={12} /></button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="gtr-card gth2-card">
            <div className="gtr-cardh">
              <span className="gth2-h">Platform activity <span className="gth2-live"><span className="gth2-live-dot" />Live</span></span>
            </div>
            <div className="gth2-chips" role="tablist">
              {KIND_CHIPS.map(c => {
                const n = kindCount(c.key);
                return (
                  <button key={c.label} type="button" role="tab" aria-selected={kind === c.key}
                    className={'gth2-chip' + (kind === c.key ? ' gth2-chip--on' : '')}
                    onClick={() => setKind(c.key)}>
                    {c.label}{n != null ? <span className="gth2-chip-n">{n}</span> : null}
                  </button>
                );
              })}
            </div>

            {events === null ? <GtLoader compact /> : events.length === 0 ? (
              <div className="gtr2-empty">Nothing recorded for this filter yet.</div>
            ) : (
              <div className="gth2-feed">
                {events.map((ev) => {
                  const g = groupOf(ev.group);
                  const clickable = !!(ev.link && ev.link.ref);
                  const inner = (
                    <>
                      <span className="gth2-ev-ic" style={{ color: g.c, background: g.bg }}><i style={{ background: g.c }} /></span>
                      <span className="gth2-ev-body">
                        <span className="gth2-ev-top">
                          <b>{ev.title}</b>
                          {ev.subject ? <span className="gth2-ev-subject">{ev.subject}</span> : null}
                        </span>
                        <span className="gth2-ev-detail">
                          <span className="gth2-ev-kind" style={{ color: g.c }}>{g.l}</span>
                          {[ev.detail_line, ev.actor_display, ev.client_name].filter(Boolean).map((x, i) => <span key={i}>{x}</span>)}
                        </span>
                      </span>
                      <span className="gth2-ev-time">{relTime(ev.at)}</span>
                    </>
                  );
                  return clickable
                    ? <button key={ev.id} type="button" className="gth2-ev gth2-ev--go" onClick={() => openEvent(ev)}>{inner}</button>
                    : <div key={ev.id} className="gth2-ev">{inner}</div>;
                })}
                {hasMore ? (
                  <button type="button" className="gth2-more" onClick={loadMore} disabled={more}>
                    {more ? 'Loading…' : 'Show earlier activity'}
                  </button>
                ) : null}
              </div>
            )}
          </section>
        </div>

        <aside className="gtr2-side gth2-side">
          <GtHomeWatch watch={watch} authFetch={authFetch} />
        </aside>
      </div>
    </div>

      {noticeOpen && (
        <GtNoticeModal ids={noticeOpen} authFetch={authFetch} onClose={() => { setNoticeOpen(null); loadNotices(); }} />
      )}
      {noticeBulk && (
        <GtBulkNoticeModal ids={noticeBulk} authFetch={authFetch} onClose={() => { setNoticeBulk(null); loadNotices(); }} />
      )}
    </>
  );
}
